import { useState } from "react";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Controller } from "react-hook-form";
import { cn } from "@/app/_utils/cn";
import { ImagePlus } from "lucide-react";

interface FestivalInputProps {
  control: any;
  errors: any;
}

export const TitleInput = ({ control, errors }: FestivalInputProps) => (
  <div className="grid gap-3">
    <Label htmlFor="title">Title</Label>
    <Controller
      name="title"
      control={control}
      render={({ field }) => (
        <Input id="title" {...field} value={field.value ?? ""} placeholder="Comedy Festival" />
      )}
    />
    {errors.title && <span className="text-[12px] text-red-600">{errors.title?.message}</span>}
  </div>
);

export const DescriptionInput = ({ control, errors }: FestivalInputProps) => (
  <div className="grid gap-3">
    <Label htmlFor="description">Description</Label>
    <Controller
      name="description"
      control={control}
      render={({ field }) => (
        <textarea
          id="description"
          {...field}
          value={field.value ?? ""}
          placeholder="A weekend of stand-up across three stages…"
          rows={10}
          className={cn(
            "flex w-full resize-y appearance-none rounded-md border border-input bg-card px-3 py-2 font-sans text-[12.5px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          )}
        />
      )}
    />
    {errors.description && (
      <span className="text-[12px] text-red-600">{errors.description?.message}</span>
    )}
  </div>
);

export const ImageInput = ({
  currentImage,
  onSelect,
  tall = false,
}: {
  currentImage?: string | null;
  onSelect: (dataUrl: string | null) => void;
  tall?: boolean;
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      onSelect(null);
      return;
    }
    if (file.size > 15 * 1000 * 1024) {
      setError("Image is too large (15mb or less)");
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPreview(result);
      onSelect(result);
    };
    reader.readAsDataURL(file);
  }

  const displaySrc = preview ?? currentImage;

  return (
    <div className="flex flex-col gap-3">
      <Label>Hero image</Label>
      <div className="flex flex-col gap-3">
        {displaySrc && (
          <img
            src={displaySrc}
            alt="Festival"
            className={cn(
              "w-full rounded-md border border-border object-contain",
              tall ? "aspect-square" : "h-[100px]"
            )}
          />
        )}
        <label
          className={cn(
            "flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/40 text-[12.5px] text-muted-foreground hover:bg-muted",
            tall && !displaySrc ? "aspect-square" : "h-[38px]",
            error && "border-red-600 text-red-600"
          )}
        >
          <ImagePlus className="size-3.5" />
          Upload image
          <input type="file" accept="image/png, image/jpeg" className="hidden" onChange={handleChange} />
        </label>
      </div>
      {error && <span className="text-[12px] text-red-600">{error}</span>}
    </div>
  );
};
